import type { AgentLogEntry, AgentState } from '@/hooks/useAgent'

export type ComputerPanelMode = 'browser' | 'editor' | 'terminal' | 'access' | 'summary'
export type ComputerCompletionState = 'live' | 'waiting' | 'completed' | 'failed' | 'stopped'

export interface ComputerRuntimeFile {
  path: string
  action: 'created' | 'modified'
}

export interface ComputerPanelModel {
  mode: ComputerPanelMode
  statusLine: string
  verb: string
  target: string
  completionState: ComputerCompletionState
  activeFile: string | null
  activeCommand: string | null
  activeUrl: string | null
  taskLabel: string
  taskCounter: string | null
  progressPct: number
  runtimeFiles: ComputerRuntimeFile[]
  hasAccessActivity: boolean
  hasBrowserActivity: boolean
  hasEditorActivity: boolean
  hasTerminalActivity: boolean
}

const LIVE_STATUSES = new Set(['connecting', 'planning', 'running', 'reconnecting', 'recovering', 'waiting_approval', 'needs_takeover', 'takeover_active'])
const FAILED_STATUSES = new Set(['failed', 'fatal_failed'])
const STOPPED_STATUSES = new Set(['stopped', 'interrupted', 'stale'])

export function cleanComputerText(value: string | null | undefined): string {
  return (value ?? '')
    .replaceAll('KjÃ¸rer', 'Kjører')
    .replaceAll('KjÃ¸rte', 'Kjørte')
    .replaceAll('SÃ¸ker', 'Søker')
    .replaceAll('pÃ¥', 'på')
    .replaceAll('ennÃ¥', 'ennå')
    .replaceAll('BÃ¸r', 'Bør')
    .replaceAll('prÃ¸ves', 'prøves')
    .replaceAll('kjÃ¸ringen', 'kjøringen')
    .replaceAll('ÃƒÂ¸', 'ø')
    .replaceAll('ÃƒÂ¥', 'å')
    .replaceAll('ÃƒÂ¦', 'æ')
    .replaceAll('Ã¢â‚¬Â¦', '...')
    .replaceAll('â€¦', '...')
    .replaceAll('Â·', '·')
    .replaceAll('âœ“', '✓')
    .replaceAll('âœ—', '×')
    .replaceAll('âœ•', '×')
    .replace(/\b(?:none|null|undefined)\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

export function isNoisyComputerLine(text: string): boolean {
  const value = cleanComputerText(text)
  if (!value) return true
  return /DuckDuckGo.*(RateLimit|202)|context deadline exceeded|_asyncio\.Task|CAPTCHA|Cloudflare|privacy dialog|cookie|search input field|button index|browser-use|about:blank|pydantic|errors\.pydantic\.dev/i.test(value)
    || /^(Step\s+\d+|Success\b|Partial\b|Unknown\b|Clicked\b|Ready to\b|Looking at\b|The URL\b|I need to\b|I entered\b|Found the\b)/i.test(value)
}

export function cleanComputerSnippet(text: string | null | undefined, fallback = 'Aktivitet registrert.'): string {
  const firstUseful = cleanComputerText(text)
    .split('\n')
    .map(line => line.trim())
    .find(line => !isNoisyComputerLine(line))
  return (firstUseful || fallback).slice(0, 220)
}

function runtimeArtifactsAsFiles(state: AgentState): ComputerRuntimeFile[] {
  if (state.runtime.artifacts.length > 0) {
    return state.runtime.artifacts
      .map(artifact => ({
        path: artifact.path ?? artifact.name,
        action: 'created' as const,
      }))
      .filter(file => Boolean(file.path))
  }
  return state.files
}

function completionStateFromStatus(status: AgentState['status']): ComputerCompletionState {
  if (LIVE_STATUSES.has(status)) return status === 'waiting_approval' || status === 'needs_takeover' || status === 'takeover_active' ? 'waiting' : 'live'
  if (FAILED_STATUSES.has(status)) return 'failed'
  if (STOPPED_STATUSES.has(status)) return 'stopped'
  return 'completed'
}

function latestUsefulLog(logs: AgentLogEntry[], predicate?: (entry: AgentLogEntry) => boolean): AgentLogEntry | null {
  return [...logs]
    .reverse()
    .find(entry => (!predicate || predicate(entry)) && !isNoisyComputerLine(entry.message)) ?? null
}

function latestUsefulSemanticEvent(state: AgentState, predicate?: (event: AgentState['semanticEvents'][number]) => boolean) {
  return [...(state.semanticEvents ?? [])]
    .reverse()
    .find(event => (!predicate || predicate(event)) && !isNoisyComputerLine(`${event.title ?? ''} ${event.text}`)) ?? null
}

function semanticTarget(event: AgentState['semanticEvents'][number] | null): string | null {
  if (!event) return null
  const title = cleanComputerText(event.title)
  const text = cleanComputerText(event.text)
  if (title && text && !text.toLowerCase().startsWith(title.toLowerCase())) return `${title}: ${text}`
  return text || title || null
}

export function isBrowserSemanticEvent(event: AgentState['semanticEvents'][number]): boolean {
  const tool = cleanComputerText(event.tool).toLowerCase()
  const value = cleanComputerText(`${event.kind} ${event.title ?? ''} ${event.text}`).toLowerCase()
  return event.kind === 'browser'
    || tool.includes('browser')
    || /\b(browser|nettleser|nettside|webside)\b/.test(value)
    || /\b(åpner|navigerer|leser|sjekker)\s+(nettside|side|url)\b/.test(value)
    || /https?:\/\//i.test(value)
}

function extractUrl(text: string | null | undefined): string | null {
  const match = cleanComputerText(text).match(/https?:\/\/[^\s)]+/i)
  return match?.[0] ?? null
}

function labelForToolLog(entry: AgentLogEntry | null): string | null {
  if (!entry) return null
  if (entry.type === 'file_change') return cleanComputerSnippet(entry.message, 'Filaktivitet.')
  if (entry.tool === 'read_file') return `Reading ${String(entry.args?.path ?? '').trim() || 'file'}`
  if (entry.tool === 'write_file') return `Editing ${String(entry.args?.path ?? '').trim() || 'file'}`
  if (entry.tool === 'list_files') return `Listing ${String(entry.args?.path ?? '.').trim()}`
  if (entry.tool === 'terminal') return `Running ${String(entry.args?.command ?? '').trim() || 'command'}`
  if (entry.tool?.includes('python')) return 'Running Python'
  return cleanComputerSnippet(entry.message, 'Kjører verktøy.')
}

function basename(path: string): string {
  return path.split('/').filter(Boolean).pop() ?? path
}

export function getPreferredComputerMode(state: AgentState): ComputerPanelMode {
  return buildComputerPanelModel(state).mode
}

export function buildComputerPanelModel(state: AgentState, selectedFile?: string | null): ComputerPanelModel {
  const completionState = completionStateFromStatus(state.status)
  const isLive = completionState === 'live' || completionState === 'waiting'
  const runtimeFiles = runtimeArtifactsAsFiles(state)
  const latestFile = selectedFile ?? runtimeFiles.at(-1)?.path ?? null
  const latestTerminal = state.terminalFrames.at(-1) ?? null
  const latestBrowser = state.browserFrame
  const fileLog = latestUsefulLog(state.logs, entry => entry.type === 'file_change' || entry.tool === 'read_file' || entry.tool === 'write_file')
  const toolLog = latestUsefulLog(state.logs, entry => entry.type === 'tool_call' || entry.type === 'file_change')
  const semanticEvent = latestUsefulSemanticEvent(state)
  const fileSemanticEvent = latestUsefulSemanticEvent(state, event => event.kind === 'file')
  const browserSemanticEvent = latestUsefulSemanticEvent(state, isBrowserSemanticEvent)
  const terminalSemanticEvent = latestUsefulSemanticEvent(state, event => event.kind === 'terminal')

  const hasAccessActivity = Boolean(state.pendingApproval) || state.runtime.approvals.length > 0
  const hasBrowserActivity = Boolean(latestBrowser || browserSemanticEvent)
  const hasBrowserTakeover = latestBrowser?.status === 'takeover' || state.browserSession?.status === 'needs_takeover' || state.browserSession?.status === 'takeover_active'
  const hasEditorActivity = Boolean(latestFile || fileLog)
  const hasTerminalActivity = Boolean(latestTerminal) || Boolean(latestUsefulLog(state.logs, entry => {
    const tool = entry.tool?.toLowerCase() ?? ''
    return tool.includes('terminal') || tool.includes('python') || tool.includes('bash') || tool.includes('run_code')
  }))

  let mode: ComputerPanelMode = 'summary'
  if (hasAccessActivity) mode = 'access'
  else if (hasBrowserTakeover) mode = 'browser'
  else if (semanticEvent && isBrowserSemanticEvent(semanticEvent)) mode = 'browser'
  else if (hasEditorActivity) mode = 'editor'
  else if (hasBrowserActivity) mode = 'browser'
  else if (hasTerminalActivity) mode = 'terminal'
  else if (isLive) mode = 'summary'

  const activeCommand = latestTerminal?.command ? cleanComputerText(latestTerminal.command) : null
  const browserTargetText = semanticTarget(browserSemanticEvent)
  const activeUrl = latestBrowser?.url ? cleanComputerText(latestBrowser.url) : extractUrl(browserTargetText)
  const approvalTarget = state.pendingApproval?.description
    ?? (state.runtime.approvals.at(-1)?.description ? String(state.runtime.approvals.at(-1)?.description) : null)

  let verb = 'Reviewing'
  let target = 'oppgaven'
  if (mode === 'access') {
    verb = 'Waiting for approval'
    target = cleanComputerSnippet(approvalTarget, 'Godkjenning kreves.')
  } else if (mode === 'editor') {
    verb = fileLog?.tool === 'read_file' ? 'Reading file' : 'Editing file'
    target = latestFile ?? cleanComputerSnippet(semanticTarget(fileSemanticEvent) ?? labelForToolLog(fileLog), 'fil')
  } else if (mode === 'browser') {
    verb = hasBrowserTakeover
      ? state.browserSession?.status === 'takeover_active'
        ? 'User has control'
        : 'Needs help in browser'
      : latestBrowser?.status === 'done'
        ? 'Reviewing browser'
        : 'Using browser'
    target = cleanComputerSnippet(
      browserTargetText
        ?? latestBrowser?.title
        ?? latestBrowser?.url
        ?? latestBrowser?.action
        ?? latestBrowser?.message,
      'nettleser',
    )
  } else if (mode === 'terminal') {
    verb = latestTerminal?.status === 'running' ? 'Running command' : 'Shell'
    target = activeCommand || cleanComputerSnippet(semanticTarget(terminalSemanticEvent) ?? labelForToolLog(toolLog), 'Ingen kommando kjører')
  } else if (completionState === 'completed') {
    verb = 'Task completed'
    target = cleanComputerSnippet(semanticTarget(semanticEvent), 'Resultatet er levert')
  } else if (completionState === 'failed') {
    verb = 'Task failed'
    target = cleanComputerSnippet(semanticTarget(semanticEvent) || state.currentActionLabel || state.currentTask, 'Kjøringen stoppet med feil')
  } else if (completionState === 'stopped') {
    verb = 'Task stopped'
    target = cleanComputerSnippet(semanticTarget(semanticEvent) || state.currentActionLabel || state.currentTask, 'Kjøringen er stoppet')
  } else {
    verb = semanticEvent?.title ? cleanComputerText(semanticEvent.title) : 'Working'
    target = cleanComputerSnippet(semanticTarget(semanticEvent) || state.currentActionLabel || state.currentTask, 'Starter oppgaven')
  }

  const totalTasks = state.liveTasks?.length ?? 0
  const doneTasks = state.liveTasks?.filter(task => task.status === 'done').length ?? 0
  const activeTask = state.liveTasks?.find(task => task.status === 'active') ?? state.liveTasks?.at(-1)
  const taskLabel = cleanComputerSnippet(
    activeTask?.label
      ?? semanticTarget(semanticEvent)
      ?? state.currentActionLabel
      ?? state.currentTask
      ?? (completionState === 'completed' ? 'Oppgave fullført' : 'Sine arbeider'),
    completionState === 'completed' ? 'Oppgave fullført' : 'Sine arbeider',
  )
  const taskCounter = totalTasks > 0 ? `${doneTasks}/${totalTasks}` : null
  const progressPct = totalTasks > 0
    ? Math.max(5, Math.min(100, (doneTasks / totalTasks) * 100))
    : completionState === 'completed'
      ? 100
      : completionState === 'failed' || completionState === 'stopped'
        ? 100
        : 55

  return {
    mode,
    statusLine: `Sine is using ${mode === 'editor' ? 'Editor' : mode === 'access' ? 'Access' : mode === 'summary' ? 'Computer' : mode[0].toUpperCase() + mode.slice(1)} | ${verb}${target ? ` ${target}` : ''}`,
    verb,
    target,
    completionState,
    activeFile: latestFile,
    activeCommand,
    activeUrl,
    taskLabel: mode === 'editor' && latestFile ? `${verb} ${basename(latestFile)}` : taskLabel,
    taskCounter,
    progressPct,
    runtimeFiles,
    hasAccessActivity,
    hasBrowserActivity,
    hasEditorActivity,
    hasTerminalActivity,
  }
}
